<script setup>
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { useI18n } from 'vue-i18n';

defineProps({
  generatedContent: { type: String, default: '' },
  adjustmentOpen: { type: Boolean, default: false },
  actionsDisabled: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'openAdjustment', 'accept', 'discard']);
const { formatMessage } = useMessageFormatter();
const { t } = useI18n();
const jarvisThinkingLogo = '/brand-assets/jarvis-thinking-logo.png';
</script>

<template>
  <section
    class="copilot-mobile-suggestion w-full overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 shadow-sm"
  >
    <header class="flex items-center gap-2 px-3 pb-2 pt-3">
      <img
        :src="jarvisThinkingLogo"
        alt=""
        class="size-10 shrink-0 rounded-full object-cover"
      />
      <h3 class="min-w-0 flex-1 text-sm font-medium text-n-slate-12">
        {{ t('CONVERSATION.REPLYBOX.JARVIS_SUGGESTION_TITLE') }}
      </h3>
      <button
        type="button"
        class="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
        :aria-label="t('GENERAL.CLOSE')"
        @click="emit('close')"
      >
        <i class="i-lucide-x size-4" />
      </button>
    </header>

    <div class="px-3 pb-3">
      <div class="max-h-56 overflow-y-auto pr-1">
        <p
          v-dompurify-html="formatMessage(generatedContent, false)"
          v-twemoji
          class="m-0 text-sm leading-6 text-n-slate-12"
        />
      </div>

      <button
        v-if="!adjustmentOpen"
        type="button"
        class="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-n-slate-11 transition-colors hover:text-n-brand"
        @click="emit('openAdjustment')"
      >
        <i class="i-lucide-sliders-horizontal size-4" aria-hidden="true" />
        <span>{{ t('CONVERSATION.REPLYBOX.JARVIS_ADJUSTMENT_LABEL') }}</span>
        <i class="i-lucide-chevron-down size-4" aria-hidden="true" />
      </button>

      <div v-else class="copilot-mobile-adjustment mt-3">
        <slot name="editor" />
      </div>
    </div>

    <footer
      class="flex items-center justify-end gap-2 border-t border-n-weak px-3 py-2.5"
    >
      <NextButton
        :label="t('GENERAL.DISCARD')"
        class="!bg-n-ruby-9 !px-3 !text-white hover:!bg-n-ruby-10"
        solid
        sm
        :disabled="actionsDisabled"
        @click="emit('discard')"
      />
      <NextButton
        :label="t('GENERAL.ACCEPT')"
        class="bg-n-blue-9 text-white"
        solid
        sm
        :disabled="actionsDisabled"
        @click="emit('accept')"
      />
    </footer>
  </section>
</template>
